export const isDevelopment = window.location.hostname === "app.dev.waterlyapp.com";
export const isLocal = window.location.hostname === "localhost";
export const isLocalOrDevelopment = isLocal || isDevelopment;

export type FeatureFlagDefinition<TFeatureFlagName extends string = string> = {
  defaultEnabled: boolean;
  dependencies?: readonly TFeatureFlagName[];
  description?: string;
};

const defineFeatureFlag = <const TDependencies extends readonly string[] = readonly []>(
  defaultEnabled: boolean,
  description?: string,
  dependencies?: TDependencies,
): FeatureFlagDefinition<TDependencies[number]> => ({
  defaultEnabled,
  dependencies,
  description,
});

const defineFeatureFlags = <
  const TFeatureFlagDefinitions extends Record<string, FeatureFlagDefinition<string>>,
>(
  definitions: TFeatureFlagDefinitions & {
    [K in keyof TFeatureFlagDefinitions]: FeatureFlagDefinition<
      Extract<keyof TFeatureFlagDefinitions, string>
    >;
  },
): TFeatureFlagDefinitions => definitions;

export const featureFlagDefinitions = defineFeatureFlags({
  showSampleManagement: defineFeatureFlag(
    false,
    [
      "Shows the legacy Sample Management nav button and system route for AM Water systems.",
      "No effect on non-AM Water systems.",
    ].join("\n"),
  ),
  enableScheduleManagement: defineFeatureFlag(
    false,
    [
      "Enables activity schedule routes, the scheduled items dashboard route, and the header quick-view badge for pending scheduled items.",
    ].join("\n"),
  ),
  enableSampleManagement: defineFeatureFlag(
    false,
    [
      "Enables the top-level Sample Schedule and Sample Management routes and turns on schedule-type filtering in the Scheduled Items dashboard.",
    ].join("\n"),
  ),
  enableDSTIntervalHandling: defineFeatureFlag(
    false,
    [
      "Builds site interval options with daylight-saving-time-aware handling during data-entry site loading.",
    ].join("\n"),
  ),
  enableConnectivityIndicator: defineFeatureFlag(
    false,
    "Shows the connectivity indicator in the global header.",
  ),
  enableCrossSiteTransfer: defineFeatureFlag(
    false,
    [
      "Allows the metric transfer modal to target a different site before selecting the destination process and component.",
      "When off, metric transfer stays within the current site.",
    ].join("\n"),
  ),
  enableLabDataImport: defineFeatureFlag(
    false,
    "Shows the Lab data import navigation entry and enables the system-scoped lab import route.",
  ),
  enableMetricRanges: defineFeatureFlag(
    false,
    [
      "Enables metric-range and regulatory-range experiences across data entry and dashboard surfaces, including range editing and regulatory out-of-range messaging.",
    ].join("\n"),
  ),
  enableMetricRangeDataEntryRangeBar: defineFeatureFlag(
    false,
    [
      "Shows the inline metric range bar for numeric metrics in data entry when the metric has configured ranges.",
    ].join("\n"),
    ["enableMetricRanges"],
  ),
  enableFlexReporting: defineFeatureFlag(false, "Enables the new flex reporting experience."),
  enableWaturaLinks: defineFeatureFlag(
    false,
    "Shows Watura referral links in user preferences and system settings.",
  ),
  enableConstants: defineFeatureFlag(
    true,
    "Shows the constants cards in system and organization settings and the available constants panel in the metric editor.",
  ),
  enableRoundsAssetsMappings: defineFeatureFlag(
    false,
    [
      "Fetches rounds-assets mappings for the current site and shows the Assets link/menu on the site header and on process/component rows in data entry.",
    ].join("\n"),
  ),
  enableMirrorFlexForm: defineFeatureFlag(
    true,
    [
      "Lets a flex form mirror a data model site. The form is built from the site's processes, components and metrics, follows their later changes, archives with the site and survives a system clone.",
      "While linked the form is read only and the data model site is the only place to change it.",
    ].join("\n"),
  ),
  enablePhraseDashboards: defineFeatureFlag(
    true,
    "Shows phrase dashboards on My Preferences and Organization Settings pages.",
  ),
});

export type FeatureFlagsEnum = keyof typeof featureFlagDefinitions;
export type FeatureFlags = { [K in FeatureFlagsEnum]: boolean };
export type FeatureFlagDependencies = { [K in FeatureFlagsEnum]: readonly FeatureFlagsEnum[] };
export type FeatureFlagDescriptions = { [K in FeatureFlagsEnum]: string | undefined };

const featureFlagEntries = Object.entries(featureFlagDefinitions) as [
  FeatureFlagsEnum,
  FeatureFlagDefinition<FeatureFlagsEnum>,
][];

const featureFlagDefaultsMutable = {} as FeatureFlags;
const featureFlagDependenciesMutable = {} as FeatureFlagDependencies;
const featureFlagDescriptionsMutable = {} as FeatureFlagDescriptions;

featureFlagEntries.forEach(([key, definition]) => {
  featureFlagDefaultsMutable[key] = definition.defaultEnabled;
  featureFlagDependenciesMutable[key] = definition.dependencies ?? [];
  featureFlagDescriptionsMutable[key] = definition.description;
});

export const featureFlags = Object.freeze(featureFlagDefaultsMutable);
export const featureFlagDependencies = Object.freeze(featureFlagDependenciesMutable);
export const featureFlagDescriptions = Object.freeze(featureFlagDescriptionsMutable);

export const featureFlagBySystemID: Partial<{ [key in FeatureFlagsEnum]: number[] }> = {
  enableScheduleManagement: [
    10, // Lake Zebra, IL
    26, // West Dundee, IL
    899, // Arlington Heights IL
    1040, //	CRWA - Wild Wings WW
    1041, //	CRWA - Wild Wings
    1109, //	CRWA - NBRID WW
    1108, //	CRWA - LSA South Shore WW
    /** Start - Singh Operations **/
    954, // SOS - SAG
    920, // SOS - Calvert Gateway
    910, // SOS - Calvert Gateway WW
    1021, // SOS - Boones Estates WW
    1020, // SOS - Lyons Creek WW
    1022, // SOS - Maryland Manor WW
    1014, // SOS - SAG WW
    1015, // SOS - Boones Estates
    1018, // SOS - Idlewood
    1013, // SOS - Lyons Creek
    1016, // SOS - Maryland Manor
    1017, // SOS - Patuxent MHP
    1023, // SOS - Patuxent WW
    /** End - Singh Operations **/
    1115, // Golden Hills CSD
    1165, // Golden Hills Lands
    /** Start - Edinburgh, IN **/
    75, // 💧 Edinburgh, IN
    1036, // 💩 Edinburgh, IN
    1161, // 📝Supt Log
    1162, // 🏋️‍♂️ Edinburgh, IN
    /** End - Edinburgh, IN **/
    752, // Ramsey Water Company
    1163, //	EJW - Witt
    1170, //	EJW - Delbert
    915, //	Animas Water
    131, // Mukwonago, WI
    1007, // Mukwonago, WI - Wastewater,
    771, // Gurnee, IL WW
    772, // Gurnee, IL Water,
    48, // Mount Zion, IL
    119, //	Bartlett, IL Water
    1643, // Round Mountain DW
    1647, // Round Mountain WW
    1591, // Santa Paula, CA
    1931, // Edmonds, WA WW
  ],
};

export default featureFlagDefinitions;
